import { useEffect, useState } from 'react';
import { Timestamp, collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../firebaseConfig';

export type ClientExportFile = {
  name: string;
  bytes: number;
  sha256: string;
  createdAt: Timestamp;
};

export type ClientExportRecord = {
  id: string;
  exportId: string;
  exportDate: string;
  createdAt?: Date;
  photoCount: number;
  skipped: number;
  type: string;
  status?: 'building' | 'ready' | 'failed';
  progress?: { done: number; total: number };
  files?: ClientExportFile[];
  error?: string | null;
};

function mapStatus(value: unknown): ClientExportRecord['status'] | undefined {
  if (value === 'building' || value === 'ready' || value === 'failed') return value;
  return undefined;
}

function mapFiles(value: unknown): ClientExportFile[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const files: ClientExportFile[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== 'object') continue;
    const f = raw as Record<string, unknown>;
    if (typeof f.name !== 'string' || !f.name) continue;
    const bytes = typeof f.bytes === 'number' ? f.bytes : 0;
    const sha256 = typeof f.sha256 === 'string' ? f.sha256 : '';
    const createdAt =
      f.createdAt instanceof Timestamp
        ? f.createdAt
        : f.createdAt && typeof (f.createdAt as { toDate?: () => Date }).toDate === 'function'
          ? Timestamp.fromDate((f.createdAt as { toDate: () => Date }).toDate())
          : Timestamp.now();
    files.push({ name: f.name, bytes, sha256, createdAt });
  }
  return files;
}

export default function useClientExports(uid: string, limitCount: number = 50) {
  const [exports, setExports] = useState<ClientExportRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!uid) {
      setExports([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    const exportsRef = collection(db, 'clients', uid, 'exports');
    const q = query(exportsRef, orderBy('createdAt', 'desc'), limit(limitCount));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const items: ClientExportRecord[] = snapshot.docs.map((doc) => {
          const data = doc.data() as any;
          const record = { ...data, id: doc.id };
          const createdAt =
            record.createdAt && typeof record.createdAt.toDate === 'function'
              ? record.createdAt.toDate()
              : undefined;
          const progress =
            record.progress &&
            typeof record.progress === 'object' &&
            typeof record.progress.done === 'number' &&
            typeof record.progress.total === 'number'
              ? { done: record.progress.done, total: record.progress.total }
              : undefined;
          return {
            id: record.id,
            exportId: typeof record.exportId === 'string' ? record.exportId : doc.id,
            exportDate: typeof record.exportDate === 'string' ? record.exportDate : '',
            createdAt,
            photoCount: typeof record.photoCount === 'number' ? record.photoCount : 0,
            skipped: typeof record.skipped === 'number' ? record.skipped : 0,
            type: typeof record.type === 'string' ? record.type : '',
            status: mapStatus(record.status),
            progress,
            files: mapFiles(record.files),
            error: typeof record.error === 'string' ? record.error : record.error === null ? null : undefined,
          };
        });
        setExports(items);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error(err);
        setExports([]);
        setLoading(false);
        setError('Unable to load your export history.');
      }
    );

    return unsubscribe;
  }, [uid, limitCount]);

  return { exports, loading, error };
}
